import { api, json } from "./api";

export type PushNotificationState = "enabled" | "disabled" | "denied" | "needs-install" | "unsupported";

function pushSupport(): "supported" | "needs-install" | "unsupported" {
  if (typeof window === "undefined" || typeof navigator === "undefined") return "unsupported";
  const isAppleMobile = /iPhone|iPod|iPad/i.test(navigator.userAgent);
  const installed = window.matchMedia?.("(display-mode: standalone)").matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (isAppleMobile && !installed) return "needs-install";
  if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
    return "unsupported";
  }
  return "supported";
}

function urlBase64ToUint8Array(value: string) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const bytes = window.atob(base64);
  return Uint8Array.from(bytes, (character) => character.charCodeAt(0));
}

async function registerSubscription(subscription: PushSubscription) {
  await api("/api/push/subscriptions", json("POST", subscription.toJSON()));
}

export async function syncPushSubscription() {
  if (pushSupport() !== "supported" || Notification.permission !== "granted") return false;
  const registration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  if (!registration?.pushManager) return false;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return false;
  await registerSubscription(subscription);
  return true;
}

export async function getPushNotificationState(): Promise<PushNotificationState> {
  const support = pushSupport();
  if (support !== "supported") return support;
  if (Notification.permission === "denied") return "denied";
  if (Notification.permission !== "granted") return "disabled";
  const registration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  if (!registration?.pushManager) return "disabled";
  return await registration.pushManager.getSubscription() ? "enabled" : "disabled";
}

export async function enablePushNotifications(): Promise<PushNotificationState> {
  const support = pushSupport();
  if (support !== "supported") return support;
  const permission = Notification.permission === "granted"
    ? "granted"
    : await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "denied" : "disabled";

  const registration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  if (!registration.pushManager) return "unsupported";
  const current = await registration.pushManager.getSubscription();
  const subscription = current ?? await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(
      (await api<{ publicKey: string }>("/api/push/config")).publicKey,
    ),
  });
  await registerSubscription(subscription);
  return "enabled";
}

export async function disablePushNotifications(): Promise<PushNotificationState> {
  const support = pushSupport();
  if (support !== "supported") return support;
  const registration = await navigator.serviceWorker.getRegistration("/");
  const subscription = await registration?.pushManager.getSubscription();
  if (subscription) {
    if (!await subscription.unsubscribe()) throw new Error("브라우저의 푸시 구독을 해제하지 못했습니다.");
    await api("/api/push/subscriptions", json("DELETE", { endpoint: subscription.endpoint }));
  }
  return Notification.permission === "denied" ? "denied" : "disabled";
}
